import { useEffect, useRef } from "react";
import type { DoozView } from "@bing/engine-dooz";
import { playSfx } from "../sound/sfx";

/**
 * دوز's sound cues. Wired ONCE inside `DoozTable`, so it covers BOTH the solo and the online
 * container — the pattern every other table uses.
 *
 * ⚠️ EVERY CUE IS AN EXISTING BOARD-GAME CLIP, not a card one. A دوز piece is a chip landing in a
 * wooden frame, so the drop borrows تخته نرد's checker (`bgCheckerPlace`) and a completed four
 * borrows its board fanfare (`bgGammon`). Nothing new is added to the sprite: منچ's own note about
 * borrowing CARD clips for a board game is the lesson this follows.
 *
 * ⚠️ IT ADOPTS AT MOUNT SILENTLY. The first render only records a baseline, so a reconnect — which
 * mounts the table with pieces already on the board — never replays a burst of drops.
 *
 * ⚠️ KEYED ON THE MONOTONIC `moveCount`, never on `lastDrop`'s identity: every socket frame is
 * fresh JSON, so the drop object differs on every unrelated update and identity comparison would
 * re-fire the sound on a frame that has nothing to do with it (منچ paid for that one).
 *
 * The MATCH's own win/lose fanfare is NOT here — that is the shared `useOutcomeSfx`, fired by the
 * container when the end card reveals, exactly as منچ and the card games do it.
 *
 * `playSfx` already no-ops when game sound is off, the table is muted, or the master volume is
 * zero, so this honours every mute and is inert in tests that do not opt sound in.
 */
export function useDoozSfx(view: DoozView | null): void {
  const lastMove = useRef<number | null>(null);
  const lastHandsDecided = useRef<number | null>(null);
  /** Were we in the opening contest on the PREVIOUS frame? The deciding throw arrives on a frame
   *  that no longer says «contest», so this is the only thing that can attribute it. */
  const wasContest = useRef(false);

  useEffect(() => {
    if (!view) return;
    const decided = (view.wins[0] ?? 0) + (view.wins[1] ?? 0) + view.draws;

    /*
     * IS THE OPENING CONTEST THE THING THAT JUST HAPPENED?
     *
     * ⚠️ «IS `openingRoll` PRESENT?» IS THE WRONG QUESTION ON THE ONE FRAME THAT MATTERS MOST. The
     * contest resolves inside the SAME `applyMove` that rolls the deciding die, so by the time that
     * frame arrives `openingRoll` is already gone — and the throw that DECIDED the match made no
     * sound at all. Reported as «my own die's sound is intermittent»; it is not intermittent, it is
     * exactly the second thrower, every time. MEASURED on the deployed build (`probe-tie.cjs`): a
     * die mounting and throwing at 14013 ms with no audio call within three seconds either side,
     * while the two throws before it each had one.
     *
     * `wasContest` is what carries the answer across that one frame. It is the same shape as the
     * board's own «this screen watched the contest» latch — and the same lesson as the fall
     * animation: the resolving frame no longer LOOKS like a contest, so nothing may key on that.
     */
    const inContest = view.openingRoll !== undefined;
    const justResolved = !inContest && wasContest.current;
    wasContest.current = inContest;

    // Adopt at mount: record the baseline and make no noise.
    if (lastMove.current === null) {
      lastMove.current = view.moveCount;
      lastHandsDecided.current = decided;
      return;
    }

    /*
     * THE OPENING CONTEST'S OWN THROW — تخته نرد's `bgDiceLand`, the very clip its §5.1 contest
     * uses, so the two dice in this app sound like each other.
     *
     * ⚠️ IT MUST COME BEFORE THE DROP CUE. A throw bumps `moveCount` exactly as a drop does, and
     * `lastDrop` is null throughout the contest — so without this branch a throw is simply silent
     * while every other die in the app is not.
     */
    if (inContest || justResolved) {
      if (view.moveCount !== lastMove.current) {
        lastMove.current = view.moveCount;
        playSfx("bgDiceLand");
      }
      return;
    }

    // A HAND was just decided — the four-in-a-row fanfare, and nothing else for that frame (the
    // drop that made it is the same frame, and one event deserves one sound).
    if (lastHandsDecided.current !== null && decided > lastHandsDecided.current) {
      lastHandsDecided.current = decided;
      lastMove.current = view.moveCount;
      if (view.handResult?.winner !== null && view.handResult !== undefined) playSfx("bgGammon");
      return;
    }
    lastHandsDecided.current = decided;

    if (view.moveCount === lastMove.current) return;
    lastMove.current = view.moveCount;
    // A `nextHand` clears `lastDrop`, so a fresh deal is silent — there is no piece to land.
    if (view.lastDrop) playSfx("bgCheckerPlace");
  }, [view]);
}
